import { stopEvent } from '@pkg/shared/utils/ui.utils'
import { Button, type ButtonProps, Tooltip } from 'antd'
import type React from 'react'
import { FaSave } from 'react-icons/fa'
import { useFormContext, useOptionalFormContext } from '../createAppFormHook'

export type SubmitActionButtonProps = Omit<ButtonProps, 'loading' | 'disabled'> & {
  formApi?: unknown
  /** Tooltip content shown when the button is disabled due to validation */
  tooltipWhenDisabled?: React.ReactNode
  /**
   * When true (default), the button only renders if the form has changes (isDirty).
   * Set to false to always render the button regardless of dirty state.
   */
  showOnlyWhenDirty?: boolean
}

export type SubmitButtonProps = Omit<SubmitActionButtonProps, 'formApi' | 'htmlType'>

/**
 * Submit action button for toolbars/headers.
 * Uses an explicit `form` prop when provided, or falls back to form context.
 */
export function SubmitActionButton(props: SubmitActionButtonProps) {
  const {
    formApi,
    children = 'Speichern',
    tooltipWhenDisabled = 'Bitte Eingaben prüfen',
    showOnlyWhenDirty = false,
    icon = <FaSave />,
    ...rest
  } = props

  const form = useOptionalFormContext(formApi)

  return (
    <form.Subscribe
      selector={state => ({
        isSubmitting: state.isSubmitting,
        canSubmit: state.canSubmit,
        isDirty: state.isDirty,
      })}
    >
      {({ isSubmitting, canSubmit, isDirty }) => {
        const button = (
          <Button
            {...rest}
            icon={icon}
            loading={isSubmitting}
            disabled={!canSubmit}
            onClick={stopEvent(() => form.handleSubmit())}
          >
            {children}
          </Button>
        )

        if (!isDirty && showOnlyWhenDirty) {
          return null
        }

        return canSubmit ? button : <Tooltip title={tooltipWhenDisabled}>{button}</Tooltip>
      }}
    </form.Subscribe>
  )
}

/**
 * Ant Design based submit button that integrates with TanStack Form state.
 * - Shows loading while submitting
 * - Disables when the form cannot submit
 * - Shows a helpful tooltip when disabled (validation friendly)
 */
export function SubmitButton(props: SubmitButtonProps) {
  const form = useFormContext()

  return <SubmitActionButton {...props} formApi={form} htmlType="submit" />
}
